/**
 * Ficha del asistente nuevo dentro del panel del reclamo (Órdenes › reclamo), etapa 2 del agente
 * de reclamos proactivo. Muestra la última ficha del análisis, si está al día, las propuestas
 * pendientes de la conversación del reclamo (confirmar / no), el enlace a esa conversación en
 * Asistente y el voto de comparación contra el agente viejo (que sigue abajo, sin cambios).
 *
 * Lee GET /api/assistant/claims/:sellerId/:claimId/thread al abrir y cada 60 s (`poll=1`: el
 * sondeo no pide análisis). Ficha vieja sin nada en camino → "Pedir análisis" (`refresh=1`).
 * Nunca rompe el resto del panel: un error se muestra en una línea con "Reintentar".
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, ChevronDown, ChevronUp, ExternalLink, Loader2, MessageSquare, Sparkles, X } from 'lucide-react';
import { api } from '../../api/client';
import { AssistantMarkdown } from '../../components/AssistantMarkdown';
import { confirmClickAllowed } from '../assistant/decisionsView';
import type { ClaimAnalysisSections, ClaimThread, PendingCard, VoteRow } from '../assistant/shared';

const REFRESH_MS = 60_000;

/** Secciones a la vista, en este orden. */
const MAIN_SECTIONS: { key: keyof ClaimAnalysisSections; title: string }[] = [
  { key: 'plazo', title: 'Plazo' },
  { key: 'plata', title: 'Plata' },
  { key: 'recomendacion', title: 'Recomendación' },
  { key: 'respuesta_propuesta', title: 'Respuesta propuesta' },
];

/** Secciones de "Ver todo", en el orden de la ficha. */
const MORE_SECTIONS: { key: keyof ClaimAnalysisSections; title: string }[] = [
  { key: 'hechos', title: 'Hechos' },
  { key: 'dice_el_comprador', title: 'Dice el comprador' },
  { key: 'premisa', title: 'Qué se verificó' },
  { key: 'hipotesis', title: 'Sin verificar' },
  { key: 'precedentes', title: 'Casos parecidos' },
  { key: 'opciones', title: 'Opciones' },
  { key: 'que_sigue', title: 'Qué sigue' },
];

const VOTES: { key: VoteRow['vote']; label: string }[] = [
  { key: 'nuevo', label: 'Mejor el nuevo' },
  { key: 'igual', label: 'Igual' },
  { key: 'viejo', label: 'Mejor el viejo' },
  { key: 'ambos_mal', label: 'Los dos mal' },
];

const btnBase =
  'inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-lg transition-opacity disabled:opacity-50';
const btnPrimary = `${btnBase} bg-purple-600 text-white hover:opacity-90`;
const btnGhost = `${btnBase} border border-[var(--color-border)] text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-hover)]`;

function hhmm(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit', hour12: false });
}

function Section({ title, text }: { title: string; text?: string }) {
  if (!text || !text.trim()) return null;
  return (
    <div>
      <div className="text-[11px] font-semibold uppercase tracking-wide text-[var(--color-text-muted)]">
        {title}
      </div>
      <AssistantMarkdown text={text} className="text-xs text-[var(--color-text)]" />
    </div>
  );
}

/** Propuesta pendiente: Confirmar (dos clics si no se puede deshacer) o No. */
function ProposalCard({
  card,
  sessionId,
  onDone,
}: {
  card: PendingCard;
  sessionId: number;
  /** Resultado de la acción: el panel lo muestra aunque la tarjeta desaparezca al recargar. */
  onDone: (outcome: { ok: boolean; text: string } | null) => void;
}) {
  const [armedAt, setArmedAt] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState<{ ok: boolean; text: string } | null>(null);

  const confirm = async () => {
    if (busy) return;
    let result: { ok: boolean; text: string } | null = null;
    const show = (o: { ok: boolean; text: string }) => {
      result = o;
      setOutcome(o);
    };
    setBusy(true);
    setOutcome(null);
    try {
      const res = await api.post<{ executed: boolean; detail: string; error?: string }>(
        `/assistant/sessions/${sessionId}/actions/${card.id}/confirm`,
        {}
      );
      if (res.success && res.data) {
        const d = res.data;
        show(
          d.executed
            ? { ok: true, text: d.detail || 'Hecho.' }
            : { ok: false, text: d.error || d.detail || 'No se ejecutó.' }
        );
      } else {
        show({ ok: false, text: res.error || 'No se pudo confirmar la acción.' });
      }
    } catch {
      show({ ok: false, text: 'No se pudo confirmar si se hizo. Se actualiza la ficha.' });
    } finally {
      setBusy(false);
      setArmedAt(null);
      onDone(result);
    }
  };

  const cancel = async () => {
    if (busy) return;
    let result: { ok: boolean; text: string } | null = null;
    const show = (o: { ok: boolean; text: string }) => {
      result = o;
      setOutcome(o);
    };
    setBusy(true);
    setOutcome(null);
    try {
      const res = await api.post<unknown>(
        `/assistant/sessions/${sessionId}/actions/${card.id}/cancel`,
        {}
      );
      show(
        res.success
          ? { ok: true, text: 'Propuesta descartada.' }
          : { ok: false, text: res.error || 'No se pudo cancelar la acción.' }
      );
    } catch {
      show({ ok: false, text: 'No se pudo cancelar la acción.' });
    } finally {
      setBusy(false);
      setArmedAt(null);
      onDone(result);
    }
  };

  const onConfirmClick = () => {
    if (!card.irreversible) return void confirm();
    if (armedAt === null) return setArmedAt(Date.now());
    if (confirmClickAllowed(armedAt, Date.now())) void confirm();
  };

  return (
    <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-2 space-y-1.5">
      <div className="text-xs font-semibold text-[var(--color-text)]">
        {card.label}
        {card.irreversible && (
          <span className="font-normal text-amber-700 dark:text-amber-400"> · no se puede deshacer</span>
        )}
      </div>
      <div className="rounded bg-[var(--color-bg)] border border-[var(--color-border)] px-2 py-1.5 text-xs break-words text-[var(--color-text)] max-h-48 overflow-y-auto">
        <AssistantMarkdown text={card.preview} />
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <button
          className={btnPrimary}
          disabled={busy || !card.can_confirm}
          onClick={onConfirmClick}
          title={card.can_confirm ? undefined : 'sin permiso'}
        >
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
          {!card.can_confirm
            ? 'Confirmar (sin permiso)'
            : armedAt !== null
              ? '¿Seguro? Confirmar'
              : 'Confirmar'}
        </button>
        {armedAt !== null ? (
          <button className={btnGhost} disabled={busy} onClick={() => setArmedAt(null)}>
            No, volver
          </button>
        ) : (
          <button className={btnGhost} disabled={busy || !card.can_confirm} onClick={cancel}>
            <X size={12} /> No
          </button>
        )}
      </div>
      {outcome && (
        <div className={`text-xs ${outcome.ok ? 'text-green-600' : 'text-[var(--color-error)]'}`}>
          {outcome.ok ? '✓ ' : '✗ '}
          {outcome.text}
        </div>
      )}
    </div>
  );
}

export function ClaimAnalysisPanel({ mlClaimId, sellerId }: { mlClaimId: number | string; sellerId: number }) {
  const navigate = useNavigate();
  const [thread, setThread] = useState<ClaimThread | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [opening, setOpening] = useState(false);
  const [openError, setOpenError] = useState<string | null>(null);
  const [comment, setComment] = useState('');
  const [voteBusy, setVoteBusy] = useState(false);
  const [voteMsg, setVoteMsg] = useState<{ ok: boolean; text: string } | null>(null);
  /** Resultado de la última propuesta confirmada o descartada (la tarjeta se va al recargar). */
  const [actionMsg, setActionMsg] = useState<{ pid: number; label: string; ok: boolean; text: string } | null>(null);
  const [asking, setAsking] = useState(false);
  const seq = useRef(0);

  const base = `/assistant/claims/${sellerId}/${encodeURIComponent(String(mlClaimId))}`;
  /** Reclamo que muestra el panel ahora y si sigue montado: una respuesta tardía de otro reclamo
   *  (o tras cerrar el panel) no toca el estado. */
  const keyRef = useRef(base);
  const aliveRef = useRef(true);
  const isCurrent = (key: string) => aliveRef.current && keyRef.current === key;

  /** `poll`: sondeo periódico (no pide análisis). `refresh`: botón "Pedir análisis". */
  const load = useCallback(async (mode: 'load' | 'poll' | 'refresh' = 'load') => {
    const key = base;
    if (!aliveRef.current || keyRef.current !== key) return;
    const my = ++seq.current;
    const extra = mode === 'poll' ? '&poll=1' : mode === 'refresh' ? '&refresh=1' : '';
    try {
      const res = await api.get<ClaimThread>(`${base}/thread?create=0${extra}`);
      if (my !== seq.current || !aliveRef.current || keyRef.current !== key) return;
      if (res.success && res.data) {
        setThread(res.data);
        setLoadError(false);
      } else {
        setLoadError(true);
      }
    } catch {
      if (my === seq.current && aliveRef.current && keyRef.current === key) setLoadError(true);
    } finally {
      if (my === seq.current && aliveRef.current && keyRef.current === key) setLoading(false);
    }
  }, [base]);

  useEffect(() => {
    keyRef.current = base;
    aliveRef.current = true;
    setThread(null);
    setLoading(true);
    setLoadError(false);
    setShowAll(false);
    setOpenError(null);
    setComment('');
    setVoteMsg(null);
    setActionMsg(null);
    setAsking(false);
    void load();
    // Con la pestaña del navegador oculta no se consulta (se retoma en la próxima vuelta visible).
    const t = setInterval(() => {
      if (!document.hidden) void load('poll');
    }, REFRESH_MS);
    return () => {
      clearInterval(t);
      aliveRef.current = false;
      seq.current++;
    };
  }, [load, base]);

  const openAssistant = async () => {
    if (opening) return;
    const key = base;
    setOpenError(null);
    const existing = thread?.session?.id;
    if (existing) {
      navigate(`/assistant?session=${existing}`);
      return;
    }
    setOpening(true);
    try {
      const res = await api.get<ClaimThread>(`${base}/thread?create=1`);
      if (!isCurrent(key)) return;
      const id = res.success ? res.data?.session?.id : undefined;
      if (id) navigate(`/assistant?session=${id}`);
      else setOpenError(res.error || 'No se pudo abrir la conversación del reclamo.');
    } catch {
      if (isCurrent(key)) setOpenError('No se pudo abrir la conversación del reclamo.');
    } finally {
      if (isCurrent(key)) setOpening(false);
    }
  };

  const vote = async (v: VoteRow['vote']) => {
    if (voteBusy || !thread?.analysis) return;
    const key = base;
    setVoteBusy(true);
    setVoteMsg(null);
    try {
      const res = await api.post<VoteRow>(`${base}/vote`, {
        vote: v,
        comment: comment.trim() || undefined,
        analysis_id: thread.analysis.id,
      });
      if (!isCurrent(key)) return;
      if (res.success && res.data) {
        const saved = res.data;
        setThread((t) => (t ? { ...t, my_vote: saved } : t));
        setComment('');
        setVoteMsg({ ok: true, text: 'Voto guardado. Gracias.' });
      } else {
        setVoteMsg({ ok: false, text: res.error || 'No se pudo guardar el voto.' });
      }
    } catch {
      if (isCurrent(key)) setVoteMsg({ ok: false, text: 'No se pudo guardar el voto.' });
    } finally {
      if (isCurrent(key)) setVoteBusy(false);
    }
  };

  const askAnalysis = async () => {
    if (asking) return;
    const key = base;
    setAsking(true);
    try {
      await load('refresh');
    } finally {
      if (isCurrent(key)) setAsking(false);
    }
  };

  // Ficha vieja (o sin ficha de un reclamo abierto): ¿hay un análisis en camino de verdad?
  const stale = !!thread?.fresh.stale;
  const refreshState = thread?.fresh.refresh ?? 'none';
  const updating = stale && (refreshState === 'queued' || refreshState === 'running');
  const canAsk = stale && thread?.fresh.claim_status === 'opened' && !updating;

  // Chip de estado.
  let chip: { text: string; cls: string } | null = null;
  if (thread) {
    if (thread.hidden)
      chip = { text: `Oculto: ${thread.hidden}`, cls: 'bg-[var(--color-surface)] text-[var(--color-text-muted)]' };
    else if (updating)
      chip = {
        text: thread.analysis ? 'Hay una novedad, actualizando…' : 'Analizando…',
        cls: 'bg-amber-500/15 text-amber-700 dark:text-amber-400',
      };
    else if (canAsk)
      chip = {
        text: 'No se pudo actualizar (análisis pendiente)',
        cls: 'bg-[var(--color-error)]/10 text-[var(--color-error)]',
      };
    else if (!thread.analysis)
      chip = { text: 'Sin análisis todavía', cls: 'bg-[var(--color-surface)] text-[var(--color-text-muted)]' };
    else if (stale)
      chip = { text: 'Hay una novedad', cls: 'bg-amber-500/15 text-amber-700 dark:text-amber-400' };
    else
      chip = {
        text: `Actualizado ${hhmm(thread.analysis.created_at)}`.trim(),
        cls: 'bg-green-500/15 text-green-700 dark:text-green-400',
      };
  }

  const a = thread?.analysis ?? null;
  const pending = (thread?.pending ?? []).filter((p) => p.status === 'pending');
  const sessionId = thread?.session?.id ?? null;
  const myVote = thread?.my_vote ?? null;
  const myVoteLabel = myVote ? VOTES.find((v) => v.key === myVote.vote)?.label ?? myVote.vote : null;

  return (
    <div className="border-t border-[var(--color-border)] px-4 py-2 shrink-0 max-h-[40vh] overflow-y-auto space-y-2">
      <div className="flex items-center gap-2 flex-wrap">
        <Sparkles size={13} className="text-purple-600 shrink-0" />
        <span className="text-xs font-semibold text-[var(--color-text)]">Análisis del asistente</span>
        {chip && <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${chip.cls}`}>{chip.text}</span>}
        {canAsk && (
          <button className={btnGhost} disabled={asking} onClick={() => void askAnalysis()}>
            {asking ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
            Pedir análisis
          </button>
        )}
        {loading && !thread && <Loader2 size={12} className="animate-spin text-[var(--color-text-muted)]" />}
        <button
          className="ml-auto p-1 rounded hover:bg-[var(--color-surface-hover)] text-[var(--color-text-muted)]"
          onClick={() => setCollapsed((c) => !c)}
          title={collapsed ? 'Mostrar' : 'Ocultar'}
        >
          {collapsed ? <ChevronDown size={14} /> : <ChevronUp size={14} />}
        </button>
      </div>

      {!collapsed && (
        <>
          {loadError && (
            <div className="flex items-center gap-2 text-xs text-[var(--color-error)]">
              No se pudo cargar el análisis
              <button className={btnGhost} onClick={() => { setLoading(true); void load(); }}>
                Reintentar
              </button>
            </div>
          )}

          {a && (
            <div className="space-y-2">
              {a.summary_line && (
                <p className="text-xs font-bold text-[var(--color-text)]">{a.summary_line}</p>
              )}
              {MAIN_SECTIONS.map((s) => (
                <Section key={s.key} title={s.title} text={a.sections?.[s.key]} />
              ))}
              {showAll && MORE_SECTIONS.map((s) => (
                <Section key={s.key} title={s.title} text={a.sections?.[s.key]} />
              ))}
              <button
                className="text-[11px] text-[var(--color-primary)] hover:underline inline-flex items-center gap-1"
                onClick={() => setShowAll((v) => !v)}
              >
                {showAll ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
                {showAll ? 'Ver menos' : 'Ver todo'}
              </button>
            </div>
          )}

          {sessionId !== null && pending.length > 0 && (
            <div className="space-y-2">
              {pending.map((p) => (
                <ProposalCard
                  key={`${base}:${p.id}`}
                  card={p}
                  sessionId={sessionId}
                  onDone={(o) => {
                    if (!isCurrent(base)) return;
                    setActionMsg(o ? { pid: p.id, label: p.label, ...o } : null);
                    void load();
                  }}
                />
              ))}
            </div>
          )}

          {actionMsg && !pending.some((p) => p.id === actionMsg.pid) && (
            <div className={`text-xs ${actionMsg.ok ? 'text-green-600' : 'text-[var(--color-error)]'}`}>
              {actionMsg.ok ? '✓ ' : '✗ '}
              {actionMsg.label}: {actionMsg.text}
            </div>
          )}

          {thread && (
            <div className="flex items-center gap-3 flex-wrap">
              <button className={btnPrimary} disabled={opening} onClick={openAssistant}>
                {opening ? <Loader2 size={12} className="animate-spin" /> : <MessageSquare size={12} />}
                Preguntar al asistente
              </button>
              {sessionId !== null && (
                <a
                  href={`/assistant?session=${sessionId}`}
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(`/assistant?session=${sessionId}`);
                  }}
                  className="text-[11px] text-[var(--color-primary)] hover:underline inline-flex items-center gap-1"
                >
                  <ExternalLink size={11} /> abrir en Asistente
                </a>
              )}
            </div>
          )}
          {openError && <div className="text-xs text-[var(--color-error)]">{openError}</div>}

          {a && (
            <div className="rounded-lg border border-[var(--color-border)] p-2 space-y-1.5">
              <div className="text-xs text-[var(--color-text-secondary)]">
                ¿Cómo estuvo el asistente nuevo frente al viejo?
              </div>
              <div className="flex flex-wrap gap-1.5">
                {VOTES.map((v) => (
                  <button
                    key={v.key}
                    className={myVote?.vote === v.key ? btnPrimary : btnGhost}
                    disabled={voteBusy}
                    onClick={() => void vote(v.key)}
                  >
                    {v.label}
                  </button>
                ))}
              </div>
              <input
                type="text"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Comentario (opcional)"
                maxLength={1000}
                className="w-full text-xs px-2 py-1 rounded border border-[var(--color-border)] bg-[var(--color-bg)] text-[var(--color-text)] outline-none focus:border-[var(--color-primary)]"
              />
              {myVote && (
                <div className="text-[11px] text-[var(--color-text-muted)]">
                  Tu voto: {myVoteLabel}
                  {myVote.comment ? ` — "${myVote.comment}"` : ''}
                </div>
              )}
              {voteMsg && (
                <div className={`text-[11px] ${voteMsg.ok ? 'text-green-600' : 'text-[var(--color-error)]'}`}>
                  {voteMsg.text}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default ClaimAnalysisPanel;
